import type { MutableRefObject } from 'react';
import { useLayoutEffect, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';

import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';

import { usePageState } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { isPageOrBlogPost } from '@confluence/content-types-utils/entry-points/isPageOrBlogpost.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { expVal } from '@confluence/feature-experiments';

import { FORGE_MODULE_PAGE_BANNER } from '../ForgeModuleType.ts';

import { useExtensionList } from './useExtensionList.tsx';

export const useForgePageBannerObserver = (
	isViewPage: boolean,
	isNav4Enabled: boolean,
	isEdit?: boolean,
): {
	isForgePageBannerFixed: boolean;
	hasPreloadedForgePageBanners: boolean | undefined;
	fixedForgePageBannerRef: MutableRefObject<null>;
	isForgePageBannerFullWidth: boolean;
	shouldHideForgePageBanner: boolean | undefined;
} => {
	const [{ spaceKey, contentType }] = usePageState();
	const routeDataRef = useRouteDataRef();
	const isLivePage = useIsCurrentPageLive();
	const renderServerPlaceholder = useIsSSROrHydrating();

	const { extensions, isSSRResult } = useExtensionList({
		moduleType: FORGE_MODULE_PAGE_BANNER,
	});

	// For Nav 3, we expand the banner to full width when the side nav is not present. This is the case for
	// company hub and when url has mode=global, such as on custom homepage.
	// For Nav 4, the side nav is always present - so the banner is never full width)
	const isSideNavPresent =
		isNav4Enabled ||
		isCompanyHubSpaceKey(spaceKey) ||
		routeDataRef.current?.queryParams?.mode === 'global';

	const { isForgePageBannerFixed, fixedForgePageBannerRef } = useForgePageBannerObserverHelper(
		isViewPage,
		isLivePage,
		renderServerPlaceholder,
		extensions,
		isEdit,
	);

	return {
		hasPreloadedForgePageBanners: isSSRResult ? extensions.length > 0 : undefined,
		isForgePageBannerFixed,
		fixedForgePageBannerRef,
		isForgePageBannerFullWidth: isSideNavPresent,
		shouldHideForgePageBanner: contentType && !isPageOrBlogPost(contentType),
	};
};

const useForgePageBannerObserverHelper = (
	isViewPage: boolean,
	isLivePage,
	renderServerPlaceholder,
	extensions,
	isEdit?: boolean,
) => {
	const fixedForgePageBannerRef = useRef(null);
	const lastLoggedForgePageBannerHeightRef = useRef<number | undefined>(undefined);

	const isForgePageBannerFixed = isViewPage && !isLivePage && !isEdit && extensions.length > 0; // extensions triggers a re-render

	const resizeObserver = useMemo(() => {
		if (!renderServerPlaceholder) {
			return new ResizeObserver(
				debounce(([entry]) => {
					if (entry) {
						const height = entry.target.clientHeight;

						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						document.documentElement.style.setProperty('--forge-page-banner-height', `${height}px`);

						if (lastLoggedForgePageBannerHeightRef.current !== height) {
							lastLoggedForgePageBannerHeightRef.current = height;

							addUFOCustomData({
								fixedForgePageBannerWrapperHeight: height,
							});
						}

						const detail = expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)
							? { detail: { height } }
							: undefined;

						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						document.dispatchEvent(new CustomEvent('forgePageBannerHeightUpdated', detail));
					}
				}, 100),
			);
		}
	}, [renderServerPlaceholder]);

	useLayoutEffect(() => {
		const fixedForgePageBanner = fixedForgePageBannerRef.current;

		if (isForgePageBannerFixed) {
			fixedForgePageBanner && resizeObserver?.observe(fixedForgePageBanner);
		}

		return () => {
			fixedForgePageBanner && resizeObserver?.unobserve(fixedForgePageBanner);
		};
	}, [isForgePageBannerFixed, resizeObserver, renderServerPlaceholder]);

	return {
		isForgePageBannerFixed,
		fixedForgePageBannerRef,
	};
};
